import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DefaultService } from '@codegen/service';
import { StatusCacheService } from '@modules/shared/services/status-cache.service';
import { ProposalStatus } from '@modules/order/order.enum';
import { ProposalService } from '@modules/order/services/proposal.service';
import { AppState } from '@modules/shared/store';
import {
  GetContractStatus,
  GetProposalStatus,
  SetContractStatus,
  SetProposalStatus,
  StatusTypes,
  StopUpdateStatus,
} from '@modules/status/store/status.action';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { interval } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';

@Injectable()
export class StatusEffects {
  private actions$ = inject(Actions);
  private statusResourceService = inject(DefaultService);
  private statusCacheService = inject(StatusCacheService);
  private store = inject<Store<AppState>>(Store);
  private proposalService = inject(ProposalService);
  private dialogRef = inject(MatDialog);

  public GetProposalStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProposalStatus>(StatusTypes.GetProposalStatus),
      map(data => data.payload),
      switchMap((proposalId: string) =>
        this.statusCacheService.getStatus(proposalId).pipe(
          switchMap(response => {
            this.isStopLoading = false;
            this.store.dispatch(
              new SetProposalStatus({
                proposalStatus: response.applicationStatus,
                proposalStatusDescription:
                  response.applicationStatusDescription,
                proposalStatusTitle: response.applicationStatusTitle,
                proposalStatusIcon: response.applicationStatusIcon,
                waitingTime: response.waitingTime,
              })
            );
            return [{ proposalId, status: response.applicationStatus.key }];
          })
        )
      ),
      switchMap(proposal => {
        return interval(this.getStatusInterval).pipe(
          switchMap(() => {
            this.statusCacheService.clearCache(proposal.proposalId);
            return this.statusResourceService.status({
              applicationId: proposal.proposalId,
            });
          }),
          takeWhile(
            () =>
              !this.isStopLoading &&
              this.proposalService.updateProposalInfoByStatus(
                proposal.status as ProposalStatus
              )
          ),
          switchMap(response => {
            return [
              new SetProposalStatus({
                proposalStatus: response.applicationStatus,
                proposalStatusDescription:
                  response.applicationStatusDescription,
                proposalStatusTitle: response.applicationStatusTitle,
                proposalStatusIcon: response.applicationStatusIcon,
                waitingTime: response.waitingTime,
              }),
            ];
          })
        );
      })
    )
  );

  public GetContractStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetContractStatus>(StatusTypes.GetContractStatus),
      map(data => data.payload),
      switchMap((contractId: string) =>
        this.statusResourceService.contractsStatus({ contractId })
      ),
      map(response => {
        this.dialogRef.closeAll();
        return new SetContractStatus({
          contractStatusTitle: response.contractStatusTitle,
          contractStatusIcon: response.contractStatusIcon,
          contractStatusDescription: response.contractStatusDescription,
          contractStatus: response.contractStatus,
        });
      })
    )
  );

  public StopUpdateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StopUpdateStatus>(StatusTypes.StopUpdateStatus),
      map(data => data.payload),
      switchMap((isStopLoading): Action[] => {
        this.isStopLoading = isStopLoading;
        return [];
      })
    )
  );

  private getStatusInterval: number = 5_000;
  private isStopLoading: boolean = false;
}
